import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldAlert, Activity, Radio, Star, LogIn } from "lucide-react";
import ChaosLogo from "@/components/ChaosLogo";
import UpdatesPanel from "@/components/UpdatesPanel";

const FEATURES = [
  {
    icon: ShieldAlert,
    label: "Tier-1 source verification",
    body: "Cross-checked editorial provenance across 400+ vetted publications and newswires.",
  },
  {
    icon: Activity,
    label: "Decentralized fact-ledger sync",
    body: "Provenance hashes cross-referenced against an open, append-only verification ledger.",
  },
  {
    icon: Radio,
    label: "Anomaly propagation tracing",
    body: "Surface manipulated narratives by detecting abnormal velocity across known signal forums.",
  },
];

/**
 * Wide-viewport desktop marketing landing.
 * Full-bleed gradient (inherited from <Shell>), wide top bar, centered hero,
 * 3-column feature grid, centered premium CTA.
 */
const MarketingLanding = ({ onLaunch }) => {
  return (
    <div
      data-testid="marketing-landing"
      className="relative w-full min-h-[100dvh] flex flex-col"
    >
      {/* Wide top bar */}
      <div className="w-full border-b border-white/[0.05]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <ChaosLogo size={34} />
            <span className="font-mono text-xs lg:text-sm tracking-[0.22em] text-[#f1f5f9] uppercase">
              <span className="text-[#cbd5e1]">CMH</span>
              <span className="text-[#FF0033] mx-1.5">//</span>
              CHAOS MEDIA HUB
            </span>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/review"
              data-testid="landing-feedback-link"
              className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#94a3b8] hover:text-amber-300 transition-colors"
            >
              <Star size={11} className="text-amber-300" />
              Leave Feedback
            </Link>
            <button
              type="button"
              data-testid="landing-signin-button"
              onClick={onLaunch}
              className="inline-flex items-center gap-1.5 border border-[#FF0033] bg-[#FF0033]/10 hover:bg-[#FF0033] text-[#FF0033] hover:text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] font-bold transition-colors"
              title="Sign in to unlock 7 free credits"
              aria-label="Sign in"
            >
              <LogIn size={12} />
              Sign in
            </button>
            <span
              data-testid="node-live-tag"
              className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#cbd5e1] flex items-center gap-2.5"
            >
              <Radio size={13} className="text-[#FF0033] chaos-pulse" />
              NODE LIVE
            </span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <main className="flex-1 w-full">
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-12 lg:pb-20 chaos-reveal">
          <div className="flex flex-col items-center text-center">
            <span className="font-mono text-[11px] lg:text-xs uppercase tracking-[0.32em] text-[#FF0033] mb-6 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 bg-[#FF0033]" />
              ALPHA VALIDATION NODE
            </span>

            <h1
              data-testid="marketing-headline"
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold uppercase tracking-tight text-[#f1f5f9] leading-[0.95] mb-6 max-w-5xl"
            >
              Isolate raw truth
              <br />
              <span className="text-[#FF0033]">from viral signal.</span>
            </h1>

            <p className="font-mono text-sm lg:text-base text-[#94a3b8] leading-relaxed max-w-2xl mb-10 lg:mb-12">
              A diagnostic terminal for media verification. Drop any headline,
              claim, or news string — Chaos Media Hub cross-references it
              against 400+ tier-1 publications, ledger logs, and dark-web
              propagation signals.
            </p>

            {/* Centered premium CTA */}
            <button
              type="button"
              onClick={onLaunch}
              data-testid="launch-terminal-button"
              className="group inline-flex items-center justify-center gap-3 bg-[#FF0033] hover:bg-white hover:text-black text-white font-bold uppercase tracking-[0.22em] px-10 lg:px-14 py-4 lg:py-5 text-sm lg:text-base border border-transparent hover:border-white transition-colors duration-200 cursor-pointer"
            >
              Launch Terminal
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>

            <p className="mt-5 font-mono text-[10px] lg:text-xs uppercase tracking-[0.26em] text-[#94a3b8]">
              7 free credits seeded on first activation
            </p>
          </div>
        </section>

        {/* Feature grid: 1 col on mobile, 3 cols on lg+ */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
          <div className="flex items-center gap-3 mb-6 lg:mb-8">
            <span className="inline-block w-2 h-2 bg-[#FF0033] chaos-pulse" />
            <h2 className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#94a3b8]">
              Diagnostic capabilities
            </h2>
            <span className="flex-1 h-px bg-white/[0.06]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#94a3b8] tabular-nums">
              03 MODULES
            </span>
          </div>

          <ul
            data-testid="feature-grid"
            className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6"
          >
            {FEATURES.map(({ icon: Icon, label, body }, i) => (
              <li
                key={label}
                data-testid={`feature-card-${i}`}
                className="group relative border border-white/[0.07] bg-white/[0.015] hover:bg-white/[0.04] hover:border-[#FF0033]/40 transition-colors p-5 lg:p-6 flex flex-col gap-3"
              >
                <span
                  aria-hidden
                  className="absolute -top-px -left-px w-3 h-3 border-t border-l border-[#FF0033]"
                />
                <span
                  aria-hidden
                  className="absolute -bottom-px -right-px w-3 h-3 border-b border-r border-white/10 group-hover:border-[#FF0033] transition-colors"
                />
                <div className="flex items-center justify-between">
                  <Icon size={18} className="text-[#FF0033]" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#94a3b8] tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-mono text-sm uppercase tracking-[0.16em] text-[#f1f5f9] leading-snug">
                  {label}
                </h3>
                <p className="text-[13px] text-[#94a3b8] font-mono leading-relaxed">
                  {body}
                </p>
              </li>
            ))}
          </ul>
        </section>

        {/* Operator Changelog — visible on desktop for anonymous visitors so
           they can preview platform velocity before sign-in. Hidden on
           mobile (the workspace owns that space; mobile users get the
           Updates tab in the header post-signin). */}
        <section className="hidden md:block max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
          <div className="flex items-center gap-3 mb-6 lg:mb-8">
            <span className="inline-block w-2 h-2 bg-[#FF0033] chaos-pulse" />
            <h2 className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#94a3b8]">
              Operator Changelog
            </h2>
            <span className="flex-1 h-px bg-white/[0.06]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#94a3b8] tabular-nums">
              LIVE
            </span>
          </div>
          <UpdatesPanel variant="inline" />
        </section>
      </main>

      {/* Footer marquee */}
      <footer className="border-t border-white/[0.06] w-full">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 font-mono text-[10px] uppercase tracking-[0.24em] text-[#94a3b8] flex items-center justify-between gap-3 flex-wrap">
          <span className="flex items-center gap-3">
            <span>BUILD 0.1.0</span>
            <span className="text-[#cbd5e1] normal-case tracking-normal text-[11px]" data-testid="marketing-footer-signature">
              © 2026 RMC Tech. CMH <span className="text-[#FF0033]">//</span> Chaos Media Hub. All rights reserved.
            </span>
          </span>
          <span className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 bg-[#FF0033] chaos-pulse" />
            NODE SYNC NOMINAL
          </span>
          <span className="flex items-center gap-3">
            <span
              className="hidden sm:inline"
              data-testid="marketing-footer-location"
            >
              Built in Chicago, Illinois
            </span>
          </span>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLanding;
